import { Expense } from "@finway-group/shared/lib/models"
import { InvoiceLinkRequest } from "@finway-group/shared/lib/models/expense/linkRequest.interface"
import { useMutation, useQuery, UseQueryOptions } from "@tanstack/react-query"
import { invalidatePurchaseOrderDetail, invalidatePurchaseOrderLists } from "Features/pages/purchaseOrders/queries/purchaseOrder.queries"
import { queryClient } from "Shared/config/reactQueryConfig"
import ExpenseHttpService from "Shared/services/expense.http.service"
import { onExpenseUpdate } from "Shared/store/actions/expense/expenseActions"
import { useDispatch } from "react-redux"
import { queryGet, queryPost } from "../../utils/reactQuery.utils"
import { CacheEnum } from "../consts"

const MODEL = "expenses"
const staleTime = 60 * 1000
type Options = Omit<UseQueryOptions<Expense>, "queryKey" | "queryFn" | "select" | "staleTime">

export const useExpense = (expenseId: string, opts?: Options) =>
    useQuery({
        ...opts,
        queryKey: [CacheEnum.EXPENSES, expenseId],
        queryFn: () => queryGet(`${MODEL}/${expenseId}`),
        select: (expense: Expense) => new Expense(expense),
        staleTime,
    })

export const useExpensesByIds = ({ ids }: { ids: Array<string> }) =>
    useQuery({
        queryKey: [CacheEnum.EXPENSES_MANY, ids],
        queryFn: ({ signal }) => ExpenseHttpService.fetchExpenses(`&_id[in]=${ids}`, undefined, undefined, undefined, { signal }),
        select: (result: { expenses: Array<Expense> }) => result.expenses,
        staleTime,
        enabled: ids.length > 0,
    })

export const invalidateExpense = async (expenseId: string) => {
    await Promise.all([
        queryClient.invalidateQueries({
            queryKey: [CacheEnum.EXPENSES, expenseId],
        }),
        queryClient.invalidateQueries({
            queryKey: [CacheEnum.EXPENSES_MANY],
        }),
    ])
}

export const useLinkPurchaseOrderMutation = () => {
    const dispatch = useDispatch()
    return useMutation<Expense, Error, { expenseId: string; linkRequest: InvoiceLinkRequest }>({
        mutationFn: async ({ expenseId, linkRequest }: { expenseId: string; linkRequest: InvoiceLinkRequest }) => {
            const response = await queryPost<InvoiceLinkRequest, Expense>(`/${MODEL}/${expenseId}/link-purchase-orders`, linkRequest)
            return new Expense(response)
        },
        onSuccess: async (data, variables) => {
            const { expenseId, linkRequest } = variables
            const promises = [
                invalidatePurchaseOrderLists(),
                invalidateExpense(expenseId),
                ...linkRequest.links.map((link) => invalidatePurchaseOrderDetail(link.poId)),
                ...linkRequest.unlinks.map((unlink) => invalidatePurchaseOrderDetail(unlink.poId)),
                dispatch(onExpenseUpdate(data)),
            ]

            await Promise.all(promises)
        },
    })
}
